import {BannerConstantJs} from 'OK/banners/bannerNew/utils/BannerConstantJs';
import {BannerTransportJs} from 'OK/banners/bannerNew/transport/BannerTransportJs';
import {BannerCfgJs} from 'OK/banners/bannerNew/config/BannerCfgJs';
import {getEffectiveViewportHeight} from 'OK/banners/bannerNew/utils/BannerCommon';
import {CurrentUserCfg} from 'OK/CurrentUserCfg';
import * as dom from 'OK/utils/dom';
import {getClientCurrentTheme, Theme} from "OK/theme";

function getCompactFooterHeight() {
    const footer = dom.firstByClass<HTMLElement>(document.body, 'compact-footer');
    if (!footer) {
        return 0;
    } else {
        return footer.offsetHeight;
    }
}

let PERSONAL_DATA_PARAMETER_BY_KEY: Record<string, string> = {};

PERSONAL_DATA_PARAMETER_BY_KEY[BannerConstantJs.PERSONAL_DATA_ID_KEY] = "user_id";
PERSONAL_DATA_PARAMETER_BY_KEY[BannerConstantJs.PERSONAL_DATA_NAME_KEY] = "user_name";
PERSONAL_DATA_PARAMETER_BY_KEY[BannerConstantJs.PERSONAL_DATA_DAYS_TO_BIRTH_KEY] = "days_to_birth";
PERSONAL_DATA_PARAMETER_BY_KEY[BannerConstantJs.PERSONAL_DATA_BIRTH_DAY_KEY] = "days_birth";
PERSONAL_DATA_PARAMETER_BY_KEY[BannerConstantJs.PERSONAL_DATA_BIRTH_MONTH_KEY] = "month_birth";

export class BannerParamsJs {
    private withUniqueId: boolean;
    private withPersonalData: boolean;
    private withLastApps: boolean;
    private withViewportHeight = false;
    private considerCompactFooter = false;
    private callbacksEnabled = false;
    private withDarkThemeParam = false;
    private withFpidParam = false;

    constructor(withUniqueId: boolean, withPersonalData: boolean, withLastApps: boolean) {
        this.withUniqueId = withUniqueId;
        this.withPersonalData = withPersonalData;
        this.withLastApps = withLastApps;
    }

    asJSON(): Record<string, string> {
        const params: Record<string, string> = {};
        const userCfg = CurrentUserCfg.getInstance();
        if (userCfg !== null) {
            const json: Record<string, string | number> = userCfg.getBannerParameters() || {};
            const t = json[BannerConstantJs.TARGETING_PARAMETER];
            if (t) {
                BannerTransportJs.appendParam(params, BannerConstantJs.TARGETING_PARAMETER, t);
            } else {
                const age = json[BannerConstantJs.USER_AGE];
                if (age) {
                    BannerTransportJs.appendParam(params, BannerConstantJs.USER_AGE, age);
                    BannerTransportJs.appendParam(params, BannerConstantJs.USER_GENDER, json[BannerConstantJs.USER_GENDER]);
                }
            }

            const uniqueId = json[BannerConstantJs.UNIQUE_ID_KEY];
            if (this.withUniqueId && uniqueId !== null) {
                BannerTransportJs.appendParam(params, BannerConstantJs.UNIQUE_ID_KEY, uniqueId);
            }

            const personalData = BannerParamsJs.getPersonalData(json);
            if (this.withPersonalData && personalData !== null) {
                BannerTransportJs.appendParam(params, BannerConstantJs.PERSONAL_DATA_KEY, personalData);
            }

            const language = userCfg.getLanguage();
            if (language) {
                /* Добавляем параметр выбранного языка */
                BannerTransportJs.appendParam(params, BannerConstantJs.LANGUAGE, language);
            }
        }
        if (this.withLastApps) {
            const lastApps = BannerCfgJs.getLastApplications();
            if (lastApps !== null) {
                params[BannerConstantJs.WITH_LAST_APPS] = lastApps;
            }
        }

        if (this.withViewportHeight) {
            let height = getEffectiveViewportHeight();
            if (this.considerCompactFooter) {
                height -= getCompactFooterHeight();
            }
            params[BannerConstantJs.VIEWPORT_HEIGHT] = height + '';
        }

        const experiment = BannerCfgJs.getCurrentExperiment();
        if (experiment) {
            params[BannerConstantJs.EXPR] = experiment;
        }
        if (this.isWithDarkThemeParam()) {
            const currentTheme = getClientCurrentTheme();
            let isDarkTheme = false;
            if (currentTheme === Theme.DARK) {
                isDarkTheme = true;
            } else if (currentTheme === Theme.AUTO) {
                isDarkTheme = this.isDarkThemeSelectedInBrowser();
            }
            if (isDarkTheme) {
                params[BannerConstantJs.IS_DARK_THEME_PARAM_NAME] = '1';
            }
        }
        if (this.isWithFpidParam()) {
            const fpid = this.getFpid();
            if (fpid) {
                params[BannerConstantJs.FPID_PARAM] = fpid;
            }
        }
        return params;
    }

    private isDarkThemeSelectedInBrowser() {
        return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    };

    private getFpid() {
        return window?.rb_sync?.id || null
    }

    setWithViewportHeight(withViewportHeight: boolean) {
        this.withViewportHeight = withViewportHeight;
    }

    setConsiderCompactFooter() {
        this.considerCompactFooter = true;
    }

    isCallbacksEnabled(): boolean {
        return this.callbacksEnabled;
    }

    setCallbacksEnabled(callbacksEnabled: boolean) {
        this.callbacksEnabled = callbacksEnabled;
    }

    isWithDarkThemeParam(): boolean {
        return this.withDarkThemeParam;
    }

    setWithDarkThemeParam(withDarkThemeParam: boolean) {
        this.withDarkThemeParam = withDarkThemeParam;
    }

    isWithFpidParam(): boolean {
        return this.withFpidParam;
    }

    setWithFpidParam(withFpidParam: boolean) {
        this.withFpidParam = withFpidParam;
    }

    static getPersonalData(json: Record<string, unknown>): (string|null) {
        const personalData = json[BannerConstantJs.PERSONAL_DATA_KEY] as Record<string, string | number>;
        if (!personalData) {
            return null;
        }

        let buf: string[] = [];

        Object.getOwnPropertyNames(personalData).forEach((key: string) => {
            buf.push(`${PERSONAL_DATA_PARAMETER_BY_KEY[key]}=${personalData[key]}`);
        });

        return buf.join('&');
    }
}